<script setup lang="ts">
import { computed } from 'vue';

interface Props {
  variant?: 'success' | 'error' | 'warning' | 'info' | 'gray' | 'primary' | 'purple' | 'indigo' | 'blue';
  size?: 'sm' | 'md';
  showDot?: boolean;
  bgColor?: string;
  textColor?: string;
}

const props = withDefaults(defineProps<Props>(), {
  variant: 'gray',
  size: 'md',
  showDot: false,
  bgColor: undefined,
  textColor: undefined,
});

// Size classes
const sizeClasses = computed(() => {
  const sizes = {
    sm: 'px-2 py-0.5 text-xs',
    md: 'px-2.5 py-1 text-xs',
  };
  return sizes[props.size];
});

// Dot size classes
const dotSizeClasses = computed(() => {
  const sizes = {
    sm: 'w-1 h-1 mr-1',
    md: 'w-1.5 h-1.5 mr-1.5',
  };
  return sizes[props.size];
});

const baseClasses = 'inline-flex items-center font-semibold rounded-full leading-5';

// Variant classes for background and text
const variantClasses = computed(() => {
  // If custom colors are provided, use them
  if (props.bgColor && props.textColor) {
    return '';
  }

  const variants = {
    success: 'bg-green-500/10 text-green-400',
    error: 'bg-red-500/10 text-red-300',
    warning: 'bg-amber-400/10 text-amber-400',
    info: 'bg-blue-500/10 text-blue-400',
    gray: 'bg-base-200 text-gray-400',
    primary: 'bg-amber-400/10 text-amber-400',
    purple: 'bg-purple-500/10 text-purple-400',
    indigo: 'bg-indigo-500/10 text-indigo-400',
    blue: 'bg-blue-500/10 text-blue-400',
  };
  return variants[props.variant];
});

// Dot color classes based on variant
const dotColorClasses = computed(() => {
  const colors = {
    success: 'bg-green-500',
    error: 'bg-red-500',
    warning: 'bg-amber-400',
    info: 'bg-blue-500',
    gray: 'bg-gray-500',
    primary: 'bg-amber-400',
    purple: 'bg-purple-500',
    indigo: 'bg-indigo-500',
    blue: 'bg-blue-500',
  };
  return colors[props.variant];
});

// Custom style for custom colors
const customStyle = computed(() => {
  if (props.bgColor && props.textColor) {
    return {
      backgroundColor: props.bgColor,
      color: props.textColor,
    };
  }
  return {};
});
</script>

<template>
  <span
    :class="[baseClasses, variantClasses, sizeClasses]"
    :style="customStyle"
  >
    <span v-if="showDot" :class="[dotColorClasses, dotSizeClasses, 'rounded-full']"></span>
    <slot />
  </span>
</template>
